import {
  IConditionalField,
  IPermitTypeField,
  PermitFieldType,
} from '~/client/graph/types.generated'
import { permitChecklistCondKeyList } from '~/client/src/shared/enums/PermitChecklistConditionalKey'
import QuestionnaireType from '~/client/src/shared/enums/QuestionnaireType'
import { IResourceRecord } from '~/client/src/shared/resources/definitions/types'
import { parseConditionalKeyIds } from '~/client/src/shared/utils/conditionalKeyUtils'
import { getQuestionnaireOptionsByType } from '~/client/src/shared/utils/PermitInstructionsHelper'

import { IConditionalSelectOption } from '../types/IConditionalSelectOption'

export const getAllRulesForField = (
  allConditionalFields: IConditionalField[],
  fieldId: string,
): IConditionalField[] => {
  return allConditionalFields?.filter(cf => cf.fieldId === fieldId) || []
}

/** Partition rules into available (any ID valid) and inactive (all IDs invalid) */
export const partitionRules = (
  allRules: IConditionalField[],
  selectOptions: IConditionalSelectOption[],
): { available: IConditionalField[]; inactive: IConditionalField[] } => {
  const optionValues = new Set(selectOptions.map(opt => opt.value))
  const available: IConditionalField[] = []
  const inactive: IConditionalField[] = []

  for (const rule of allRules) {
    const ids = parseConditionalKeyIds(rule.key)
    const isAvailable = ids.some(id => optionValues.has(id))
    ;(isAvailable ? available : inactive).push(rule)
  }

  return { available, inactive }
}

export const getSelectOptions = (
  field: IPermitTypeField,
  allowedLocationIds: string[],
  materialResourceRecords?: IResourceRecord[],
): IConditionalSelectOption[] => {
  const { type, checklist, selectOptions } = field

  switch (type) {
    case PermitFieldType.Select:
      return (selectOptions || []).map(v => ({ title: v, value: v }))
    case PermitFieldType.Checklist:
      return permitChecklistCondKeyList.map(v => ({ title: v, value: v }))
    case PermitFieldType.Question:
      return getQuestionnaireOptionsByType(
        checklist.list?.[0]?.questionnaireType as QuestionnaireType,
      ).map(opt => ({ title: opt.title, value: opt.key }))
    case PermitFieldType.Equipment:
    case PermitFieldType.Location:
    case PermitFieldType.LocationInformational:
    case PermitFieldType.EquipmentInformational:
      return allowedLocationIds.map(opt => ({
        title: opt,
        value: opt,
      }))
    case PermitFieldType.MaterialResource:
    case PermitFieldType.MaterialResourceInformational:
      return (materialResourceRecords || []).map(r => ({
        title: r.name,
        value: r.id,
      }))
    default:
      return []
  }
}

export const getAvailableSelectOptions = (
  selectOptions: IConditionalSelectOption[],
  existingRuleKeys: string[],
): IConditionalSelectOption[] => {
  const usedIds = new Set(existingRuleKeys)
  return selectOptions?.filter(opt => !usedIds.has(opt.value)) || []
}

/** Flatten all rule keys (including multi-keys) into individual location IDs */
export const flattenRuleKeys = (ruleKeys: string[]): string[] => {
  return ruleKeys.flatMap(parseConditionalKeyIds)
}
